import axios from 'axios';
import Axios, { AxiosError, AxiosInstance } from 'axios';

export type HttpHeaders = {
  [key: string]: string;
};

export type RequestConfig = {
  headers?: HttpHeaders;
  data?: { [key: string]: string };
};

export class ApiConfiguration {
  baseUrl?: string;
}

export interface IApiClient {
  post<TRequest, TResponse>(
    path: string,
    payload: TRequest,
    config?: RequestConfig
  ): Promise<TResponse>;

  get<TResponse>(path: string): Promise<TResponse>;

  patch<TRequest, TResponse>(
    path: string,
    payload: TRequest,
    config?: RequestConfig
  ): Promise<TResponse>;

  delete<TResponse>(path: string, config?: RequestConfig): Promise<TResponse>;
}

export default class ApiClient implements IApiClient {
  private client: AxiosInstance;

  protected createAxiosClient(
    apiConfiguration: ApiConfiguration
  ): AxiosInstance {
    return Axios.create({
      baseURL: apiConfiguration.baseUrl,
      responseType: 'json' as const,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      timeout: 60 * 1000,
    });
  }

  constructor(apiConfiguration: ApiConfiguration) {
    this.client = this.createAxiosClient(apiConfiguration);
  }

  async post<TRequest, TResponse>(
    path: string,
    payload: TRequest,
    config?: RequestConfig | undefined
  ): Promise<TResponse> {
    try {
      const response = config
        ? await this.client.post<TResponse>(path, payload, config)
        : await this.client.post<TResponse>(path, payload);
      return response.data;
    } catch (e) {
      //TODO: handle network error
      let cause: string | undefined;
      let message: string = 'Could not complete POST Request';
      if (axios.isAxiosError(e)) {
        let data = e.response?.data as { error?: string } | undefined;
        if (data?.error) {
          cause = data.error;
        }
        message = e.message;
      }
      throw Error(message, { cause });
    }
  }

  async get<TResponse>(
    path: string,
    config?: RequestConfig | undefined
  ): Promise<TResponse> {
    try {
      const response = await this.client.get<TResponse>(path);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.log(`GET error`, error.response?.status);
      }
      //TODO: handle error
      throw error;
    }
  }

  async patch<TRequest, TResponse>(
    path: string,
    payload: TRequest,
    config?: RequestConfig | undefined
  ): Promise<TResponse> {
    try {
      const response = config
        ? await this.client.patch<TResponse>(path, payload, config)
        : await this.client.patch<TResponse>(path, payload);
      return response.data;
    } catch (e) {
      //TODO: handle network error

      throw Error('Could not complete PATCH Request');
    }
  }

  async delete<TResponse>(
    path: string,
    config?: RequestConfig | undefined
  ): Promise<TResponse> {
    try {
      const response = config
        ? await this.client.delete<TResponse>(path, config)
        : await this.client.delete<TResponse>(path);
      return response.data;
    } catch (e) {
      //TODO: handle network error
      throw Error('Could not complete DELETE Request');
    }
  }
}
